import {
  appendTransactionMessageInstructions,
  createSolanaRpc,
  createTransactionMessage,
  getSignatureFromTransaction,
  pipe,
  setTransactionMessageFeePayerSigner,
  setTransactionMessageLifetimeUsingBlockhash,
  signTransactionMessageWithSigners,
} from "@solana/kit";
import type {
  Address,
  Lamports,
  Rpc,
  SolanaRpcApi,
  TransactionSigner,
} from "@solana/kit";
import { getTransferSolInstruction } from "@solana-program/system";
import { getSolanaRpcEndpoint, LAMPORTS_PER_SOL } from "../config/constants";
import { sendSignedTransaction } from "../api/relay";
import type { TransactionResult } from "../types/solana";

/**
 * Solana service — Kit-native wrapper around the backend `/api/rpc` proxy.
 *
 * READ OPERATIONS:
 * - All read operations go through the proxy, which handles failover, caching,
 *   and rate-limiting on the backend.
 *
 * WRITE OPERATIONS:
 * - Client signs via the connected wallet's `TransactionSigner` (from
 *   `@nullaone/wallet-react`'s `useBrowserWallet`).
 * - Submission goes through the backend relay (`/api/relay/transaction`) so
 *   the server waits for confirmation before resolving — the previous
 *   client-side `rpc.sendTransaction` path returned `confirmed: true` the
 *   moment the cluster accepted the broadcast, before any state change.
 */
class SolanaService {
  private rpc: Rpc<SolanaRpcApi>;

  constructor() {
    this.rpc = createSolanaRpc(getSolanaRpcEndpoint());
  }

  /** Get the Kit RPC client. */
  public getRpc(): Rpc<SolanaRpcApi> {
    return this.rpc;
  }

  /** Get SOL balance (returns SOL, not lamports). */
  public async getBalance(address: Address): Promise<number> {
    const { value } = await this.rpc.getBalance(address).send();
    return Number(value) / LAMPORTS_PER_SOL;
  }

  /** Send SOL to another address. Signer must equal `from`. */
  public async sendSOL(
    from: Address,
    to: Address,
    amountSOL: number,
    signer: TransactionSigner,
  ): Promise<TransactionResult> {
    try {
      const { value: latestBlockhash } = await this.rpc
        .getLatestBlockhash({ commitment: "confirmed" })
        .send();

      const lamports = BigInt(
        Math.floor(amountSOL * LAMPORTS_PER_SOL),
      ) as Lamports;
      const instruction = getTransferSolInstruction({
        source: signer,
        destination: to,
        amount: lamports,
      });

      const message = pipe(
        createTransactionMessage({ version: 0 }),
        (m) => setTransactionMessageFeePayerSigner(signer, m),
        (m) => setTransactionMessageLifetimeUsingBlockhash(latestBlockhash, m),
        (m) => appendTransactionMessageInstructions([instruction], m),
      );

      const signed = await signTransactionMessageWithSigners(message);
      const relayed = await sendSignedTransaction(signed);

      void from;
      return {
        signature: getSignatureFromTransaction(signed),
        confirmed: relayed.confirmed,
      };
    } catch (error) {
      return this.handleTransactionError(error);
    }
  }

  /** Request airdrop (devnet/testnet only). */
  public async requestAirdrop(
    address: Address,
    amountSOL: number = 1,
  ): Promise<string> {
    const lamports = BigInt(amountSOL * LAMPORTS_PER_SOL) as Lamports;
    const result = await this.rpc.requestAirdrop(address, lamports).send();
    return result;
  }

  /** Get current slot. */
  public async getSlot(): Promise<bigint> {
    return this.rpc.getSlot().send();
  }

  /** Get block time for a slot. */
  public async getBlockTime(slot: bigint): Promise<bigint | null> {
    const result = await this.rpc.getBlockTime(slot).send();
    return result ?? null;
  }

  /** Handle transaction errors and convert to user-friendly format. */
  private handleTransactionError(error: unknown): TransactionResult {
    console.error("Transaction error:", error);

    const errorMessage = error instanceof Error ? error.message : String(error);

    if (errorMessage.includes("User rejected")) {
      return {
        signature: "",
        confirmed: false,
        error: "UserRejected",
        message: "Transaction was rejected by user",
      };
    }

    if (errorMessage.includes("insufficient")) {
      return {
        signature: "",
        confirmed: false,
        error: "InsufficientFunds",
        message: "Insufficient funds for transaction",
      };
    }

    if (errorMessage.includes("blockhash")) {
      return {
        signature: "",
        confirmed: false,
        error: "BlockhashNotFound",
        message: "Transaction expired, please try again",
      };
    }

    if (
      errorMessage.includes("timeout") ||
      errorMessage.includes("timed out")
    ) {
      return {
        signature: "",
        confirmed: false,
        error: "TransactionExpired",
        message: "Transaction timed out, please try again",
      };
    }

    return {
      signature: "",
      confirmed: false,
      error: "Unknown",
      message: "Transaction failed: " + errorMessage,
    };
  }
}

// Export singleton instance
export const solanaService = new SolanaService();
export default solanaService;
